<template>
    <Panel size="full" padding="p-4" :corners="false">
        <div class="flex flex-col gap-4">
            <div class="flex items-center justify-between gap-4">
                <h2 class="text-base leading-6 font-black tracking-[0.25px] uppercase">
                    {{ $t('statistics.segment_accuracy') }}
                </h2>
                <span
                    class="text-bg flex h-6 items-center bg-yellow-600 px-2 text-xs leading-4 font-black tracking-[0.25px] uppercase"
                >
                    {{ badge }}
                </span>
            </div>

            <div class="flex flex-col gap-2">
                <div
                    v-for="(result, index) in results"
                    :key="result.target"
                    class="grid grid-cols-[40px_minmax(0,1fr)] items-center gap-4"
                    :class="{ 'cursor-pointer': showRings }"
                    @click="selectedIndex = index"
                >
                    <span class="font-title text-right text-sm leading-4 font-extrabold">
                        {{ result.target }}
                    </span>
                    <div class="bg-bg relative h-6">
                        <div
                            v-if="showRings && result.accuracy > 0"
                            class="absolute inset-y-0 left-0 flex"
                            :class="
                                showRings && index === selectedIndex
                                    ? [
                                          'divide-x-2 outline-2 -outline-offset-2',
                                          positive
                                              ? 'divide-green-300 outline-green-300'
                                              : 'divide-red-300 outline-red-300',
                                      ]
                                    : ''
                            "
                            :style="{ width: `${result.accuracy}%` }"
                        >
                            <div
                                v-for="ring in ringsWithHits(result)"
                                :key="ring"
                                class="h-full"
                                :class="ringColors[ring]"
                                :style="{ width: `${ringShare(result, ring)}%` }"
                            ></div>
                        </div>
                        <div
                            v-else
                            class="absolute inset-y-0 left-0"
                            :class="positive ? 'bg-green-600' : 'bg-red-600'"
                            :style="{ width: `${result.accuracy}%` }"
                        ></div>
                        <span
                            class="absolute inset-y-0 right-4 flex items-center text-xs leading-4 font-black tracking-[0.25px]"
                        >
                            {{ Math.round(result.accuracy) }}%
                        </span>
                    </div>
                </div>
            </div>

            <div
                v-if="showRings && selected && ringsWithHits(selected).length"
                class="grid gap-2"
                :style="{
                    gridTemplateColumns: `repeat(${ringsWithHits(selected).length}, minmax(0, 1fr))`,
                }"
            >
                <div
                    v-for="ring in ringsWithHits(selected)"
                    :key="ring"
                    class="bg-bg flex h-12 items-center justify-center gap-2 text-sm leading-5 font-black tracking-[0.25px]"
                >
                    <span class="size-2" :class="ringColors[ring]"></span>
                    {{ ring }} {{ selected.rings?.[ring] ?? 0 }}
                </div>
            </div>
        </div>
    </Panel>
</template>

<script>
import Panel from '@/components/layout/Panel.vue'

export default {
    name: 'SegmentAccuracyCard',
    components: {
        Panel,
    },
    props: {
        badge: {
            type: String,
            required: true,
        },
        results: {
            type: Array,
            required: true,
        },
        positive: {
            type: Boolean,
            default: false,
        },
        showRings: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedIndex: 0,
        }
    },
    computed: {
        rings() {
            return ['S', 'D', 'T']
        },
        ringColors() {
            return this.positive
                ? { S: 'bg-green-800', D: 'bg-green-700', T: 'bg-green-600' }
                : { S: 'bg-red-800', D: 'bg-red-700', T: 'bg-red-600' }
        },
        selected() {
            return this.results[this.selectedIndex] ?? null
        },
    },
    watch: {
        results() {
            this.selectedIndex = 0
        },
    },
    methods: {
        ringsWithHits(result) {
            return this.rings.filter((ring) => result.rings?.[ring] > 0)
        },
        ringShare(result, ring) {
            const hits = Object.values(result.rings ?? {}).reduce((sum, count) => sum + count, 0)
            return hits > 0 ? ((result.rings?.[ring] ?? 0) / hits) * 100 : 0
        },
    },
}
</script>
